import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Printer, Mail, Phone, MapPin, User, Briefcase, FileText, CreditCard } from 'lucide-react'
import toast from 'react-hot-toast'

import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { useAuthStore } from '@/store/auth.store'
import { employeesService } from '@/services/employeesService'
import { Employee } from '@/api/types'

/**
 * Employee View Page
 * Professional CV-style detailed view of employee information
 */
export const EmployeesView: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { user } = useAuthStore()
  
  const [employee, setEmployee] = useState<Employee | null>(null)
  const [loading, setLoading] = useState(false)

  const branchId = user?.selectedBranch?.branchId

  useEffect(() => {
    if (!branchId || !id) return

    const fetchEmployee = async () => {
      try {
        setLoading(true)
        const data = await employeesService.getEmployeeById(branchId, Number(id))
        setEmployee(data)
      } catch (error: any) {
        console.error('Error fetching employee:', error)
        toast.error(error.response?.data?.message || 'Failed to fetch employee details')
        navigate('/employees')
      } finally {
        setLoading(false)
      }
    }

    fetchEmployee()
  }, [branchId, id, navigate])

  const handlePrint = () => {
    window.print()
  }

  const handleBack = () => {
    navigate('/employees')
  }

  const getEmployeeFullName = (emp: Employee) => {
    const firstName = emp.empFirstNameEn || emp.empFirstNameAr || ''
    const lastName = emp.empLastNameEn || emp.empLastNameAr || ''
    return `${firstName} ${lastName}`.trim() || 'N/A'
  }

  const getEmployeeInitials = (emp: Employee) => {
    const firstName = emp.empFirstNameEn || emp.empFirstNameAr || ''
    const lastName = emp.empLastNameEn || emp.empLastNameAr || ''
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'NA'
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A'
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      })
    } catch {
      return dateString
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={handleBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
        <Card>
          <CardContent className="p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <p className="mt-4 text-muted-foreground">Loading employee details...</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!employee) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={handleBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
        <Card>
          <CardContent className="p-12 text-center">
            <p className="text-muted-foreground">Employee not found</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="cv-container">
      {/* Header with Back and Print buttons */}
      <div className="flex items-center justify-between mb-6 no-print">
        <Button variant="ghost" onClick={handleBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button onClick={handlePrint} className="print:hidden">
          <Printer className="w-4 h-4 mr-2" />
          Print CV
        </Button>
      </div>

      {/* Professional CV Layout */}
      <div className="cv-wrapper bg-white print:bg-white max-w-4xl mx-auto print:max-w-full print:w-full">
        {/* CV Header Section */}
        <div className="cv-header bg-gradient-to-r from-primary to-primary/80 text-white p-8 print:p-6 print:from-primary print:to-primary print:w-full">
          <div className="flex flex-col md:flex-row gap-6 items-center md:items-start print:gap-4 print:items-center">
            <Avatar className="h-32 w-32 md:h-28 md:w-28 print:h-20 print:w-20 border-4 border-white/20 print:border-2 print:flex-shrink-0">
              <AvatarImage
                src={employee.imageUrl || employee.profileImageUrl}
                alt={getEmployeeFullName(employee)}
              />
              <AvatarFallback className="text-3xl print:text-xl bg-white/20 text-white">
                {getEmployeeInitials(employee)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 text-center md:text-left print:text-left print:flex-1">
              <h1 className="text-4xl font-bold mb-2 print:text-2xl print:mb-1 print:leading-tight">
                {getEmployeeFullName(employee)}
              </h1>
              <p className="text-xl mb-4 print:text-base print:mb-2 opacity-95 print:opacity-100">
                {employee.jobName || 'Employee'}
              </p>
              {employee.empCode && (
                <p className="text-sm opacity-90 print:text-xs print:opacity-100">
                  Employee ID: {employee.empCode}
                </p>
              )}
            </div>
            <div className="text-center md:text-right print:hidden">
              <Badge variant="secondary" className="text-sm bg-white/20 text-white border-white/30">
                {employee.isActive === 1 ? 'Active' : 'Inactive'}
              </Badge>
            </div>
          </div>
        </div>

        {/* Contact Information Bar */}
        <div className="cv-contact bg-gray-50 dark:bg-gray-900 print:bg-gray-100 border-b border-gray-200 dark:border-gray-800 print:border-gray-300 px-8 py-4 print:px-6 print:py-2 print:w-full">
          <div className="flex flex-wrap justify-center md:justify-start gap-6 print:gap-3 print:justify-start text-sm print:text-xs">
            {employee.empEmail && (
              <div className="flex items-center gap-2 print:gap-1.5">
                <Mail className="w-4 h-4 print:w-3.5 print:h-3.5 text-primary" />
                <span className="print:text-xs">{employee.empEmail}</span>
              </div>
            )}
            {employee.empMobileNo && (
              <div className="flex items-center gap-2 print:gap-1.5">
                <Phone className="w-4 h-4 print:w-3.5 print:h-3.5 text-primary" />
                <span className="print:text-xs">{employee.empMobileNo}</span>
              </div>
            )}
            {employee.companyMobileNo && (
              <div className="flex items-center gap-2 print:gap-1.5">
                <Phone className="w-4 h-4 print:w-3.5 print:h-3.5 text-primary" />
                <span className="print:text-xs">Company: {employee.companyMobileNo}</span>
              </div>
            )}
            {employee.empAddress && (
              <div className="flex items-center gap-2 print:gap-1.5">
                <MapPin className="w-4 h-4 print:w-3.5 print:h-3.5 text-primary" />
                <span className="print:text-xs">{employee.empAddress}</span>
              </div>
            )}
          </div>
        </div>

        {/* CV Content */}
        <div className="cv-content p-8 print:p-6 print:px-6 space-y-6 print:space-y-3 print:w-full">
          {/* Personal Information */}
          <section className="cv-section print:break-inside-avoid print:mb-3">
            <h2 className="cv-section-title flex items-center gap-2 mb-4 print:mb-2 print:gap-1.5">
              <User className="w-5 h-5 print:w-3.5 print:h-3.5 text-primary print:text-primary" />
              <span>Personal Information</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 print:gap-2 print:grid-cols-2">
              <div className="cv-field">
                <span className="cv-label">First Name (English)</span>
                <span className="cv-value">{employee.empFirstNameEn || 'N/A'}</span>
              </div>
              <div className="cv-field">
                <span className="cv-label">First Name (Arabic)</span>
                <span className="cv-value">{employee.empFirstNameAr || 'N/A'}</span>
              </div>
              <div className="cv-field">
                <span className="cv-label">Date of Birth</span>
                <span className="cv-value">{formatDate(employee.empDob)}</span>
              </div>
              <div className="cv-field">
                <span className="cv-label">Age</span>
                <span className="cv-value">{employee.empAge || 'N/A'}</span>
              </div>
              <div className="cv-field">
                <span className="cv-label">Birth Place</span>
                <span className="cv-value">{employee.empBirthPlace || 'N/A'}</span>
              </div>
              <div className="cv-field">
                <span className="cv-label">Gender</span>
                <span className="cv-value">{employee.genderName || 'N/A'}</span>
              </div>
              <div className="cv-field">
                <span className="cv-label">Nationality</span>
                <span className="cv-value">{employee.nationalityName || 'N/A'}</span>
              </div>
              <div className="cv-field">
                <span className="cv-label">Marital Status</span>
                <span className="cv-value">{employee.maritalStatus ? 'Married' : 'Single'}</span>
              </div>
              {employee.empAddress && (
                <div className="cv-field md:col-span-2">
                  <span className="cv-label">Address</span>
                  <span className="cv-value">{employee.empAddress}</span>
                </div>
              )}
            </div>
          </section>

          {/* Employment Information */}
          <section className="cv-section print:break-inside-avoid print:mb-3">
            <h2 className="cv-section-title flex items-center gap-2 mb-4 print:mb-2 print:gap-1.5">
              <Briefcase className="w-5 h-5 print:w-3.5 print:h-3.5 text-primary print:text-primary" />
              <span>Employment Information</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 print:gap-2 print:grid-cols-2">
              {employee.departmentName && (
                <div className="cv-field">
                  <span className="cv-label">Department</span>
                  <span className="cv-value">{employee.departmentName}</span>
                </div>
              )}
              {employee.jobName && (
                <div className="cv-field">
                  <span className="cv-label">Job Title</span>
                  <span className="cv-value">{employee.jobName}</span>
                </div>
              )}
              {employee.empPostedLocation && (
                <div className="cv-field">
                  <span className="cv-label">Posted Location</span>
                  <span className="cv-value">{employee.empPostedLocation}</span>
                </div>
              )}
              {employee.noticePeriod && (
                <div className="cv-field">
                  <span className="cv-label">Notice Period</span>
                  <span className="cv-value">{employee.noticePeriod} days</span>
                </div>
              )}
            </div>
          </section>

          {/* Contact Information */}
          <section className="cv-section print:break-inside-avoid print:mb-3">
            <h2 className="cv-section-title flex items-center gap-2 mb-4 print:mb-2 print:gap-1.5">
              <Phone className="w-5 h-5 print:w-3.5 print:h-3.5 text-primary print:text-primary" />
              <span>Contact Information</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 print:gap-2 print:grid-cols-2">
              {employee.empEmail && (
                <div className="cv-field">
                  <span className="cv-label">Email</span>
                  <span className="cv-value">{employee.empEmail}</span>
                </div>
              )}
              {employee.empMobileNo && (
                <div className="cv-field">
                  <span className="cv-label">Mobile</span>
                  <span className="cv-value">{employee.empMobileNo}</span>
                </div>
              )}
              {employee.companyMobileNo && (
                <div className="cv-field">
                  <span className="cv-label">Company Mobile</span>
                  <span className="cv-value">{employee.companyMobileNo}</span>
                </div>
              )}
              {employee.contactPersonNumber && (
                <div className="cv-field">
                  <span className="cv-label">Contact Person Number</span>
                  <span className="cv-value">{employee.contactPersonNumber}</span>
                </div>
              )}
            </div>
          </section>

          {/* Documents */}
          {(employee.iqamaNo || employee.passportNo || employee.iqamaExpiryDate || employee.passportExpiryDate) && (
            <section className="cv-section print:break-inside-avoid print:mb-3">
              <h2 className="cv-section-title flex items-center gap-2 mb-4 print:mb-2 print:gap-1.5">
                <CreditCard className="w-5 h-5 print:w-3.5 print:h-3.5 text-primary print:text-primary" />
                <span>Documents & Identification</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 print:gap-2 print:grid-cols-2">
                {employee.iqamaNo && (
                  <div className="cv-field">
                    <span className="cv-label">Iqama Number</span>
                    <span className="cv-value">{employee.iqamaNo}</span>
                  </div>
                )}
                {employee.iqamaExpiryDate && (
                  <div className="cv-field">
                    <span className="cv-label">Iqama Expiry Date</span>
                    <span className="cv-value">{formatDate(employee.iqamaExpiryDate)}</span>
                  </div>
                )}
                {employee.passportNo && (
                  <div className="cv-field">
                    <span className="cv-label">Passport Number</span>
                    <span className="cv-value">{employee.passportNo}</span>
                  </div>
                )}
                {employee.passportExpiryDate && (
                  <div className="cv-field">
                    <span className="cv-label">Passport Expiry Date</span>
                    <span className="cv-value">{formatDate(employee.passportExpiryDate)}</span>
                  </div>
                )}
                {employee.professionIqama && (
                  <div className="cv-field">
                    <span className="cv-label">Profession in Iqama</span>
                    <span className="cv-value">{String(employee.professionIqama)}</span>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Additional Information */}
          {(employee.empReferenceId || employee.empRefrence || employee.empReferenceContact) && (
            <section className="cv-section print:break-inside-avoid print:mb-3">
              <h2 className="cv-section-title flex items-center gap-2 mb-4 print:mb-2 print:gap-1.5">
                <FileText className="w-5 h-5 print:w-3.5 print:h-3.5 text-primary print:text-primary" />
                <span>Additional Information</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 print:gap-2 print:grid-cols-2">
                {employee.empReferenceId && (
                  <div className="cv-field">
                    <span className="cv-label">Reference ID</span>
                    <span className="cv-value">{employee.empReferenceId}</span>
                  </div>
                )}
                {employee.empRefrence && (
                  <div className="cv-field">
                    <span className="cv-label">Reference</span>
                    <span className="cv-value">{employee.empRefrence}</span>
                  </div>
                )}
                {employee.empReferenceContact && (
                  <div className="cv-field">
                    <span className="cv-label">Reference Contact</span>
                    <span className="cv-value">{employee.empReferenceContact}</span>
                  </div>
                )}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  )
}
